import { useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";
import { CHIEF_COMPLAINT_AREAS, areaByKey } from "../../lib/clinicalAreas";

/**
 * ChiefComplaintPicker — Pre-treatment step 2.
 *   Therapist picks the primary body area, types a free-text description,
 *   marks acute vs. chronic, and selects any secondary concerns.
 *
 * Props:
 *   value: {
 *     chief_complaint: string,
 *     chief_complaint_area: string,  // area key
 *     secondary_concerns: string[],  // area keys
 *     issue_type: "acute" | "chronic" | null,
 *   }
 *   onChange: (next) => void
 *   onSaveDraft?: () => void
 *   testIdPrefix?: string
 */
export default function ChiefComplaintPicker({ value, onChange, onSaveDraft, testIdPrefix = "complaint" }) {
  const v = value || {};
  const textareaRef = useRef(null);

  // Autofocus the area picker chips area on mount once the textarea is mounted
  useEffect(() => {
    if (!v.chief_complaint && textareaRef.current) {
      textareaRef.current.focus({ preventScroll: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only autofocus
  }, []);

  const setArea = (key) => {
    const next = { ...v, chief_complaint_area: key };
    // If selecting the same key as a secondary, remove from secondary
    next.secondary_concerns = (v.secondary_concerns || []).filter((k) => k !== key);
    onChange(next);
  };

  const toggleSecondary = (key) => {
    if (!key || key === v.chief_complaint_area) return;
    const cur = v.secondary_concerns || [];
    const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
    onChange({ ...v, secondary_concerns: next });
  };

  const areaLabel = areaByKey(v.chief_complaint_area)?.label;

  return (
    <div data-testid={`${testIdPrefix}-section`}>
      <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8 space-y-6">
        {/* Primary area chips */}
        <div>
          <div className="label-overline mb-3 text-terracotta-600">Primary area</div>
          <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-area-chips`}>
            {CHIEF_COMPLAINT_AREAS.map((a) => (
              <button
                key={a.key}
                type="button"
                onClick={() => setArea(a.key)}
                data-testid={`${testIdPrefix}-area-${a.key}`}
                className={`min-h-[40px] px-4 rounded-full border text-sm transition-all ${
                  v.chief_complaint_area === a.key
                    ? "bg-sage-500 text-white border-sage-500"
                    : "bg-white border-paper-200 text-ink-700 hover:border-sage-500/40"
                }`}
              >
                {a.label}
              </button>
            ))}
          </div>
          {areaLabel && (
            <div className="text-xs text-ink-500 mt-2" data-testid={`${testIdPrefix}-area-selected-hint`}>
              ROM tests at baseline & reassessment will be tailored to <strong>{areaLabel}</strong>.
            </div>
          )}
        </div>

        {/* Free-text complaint description */}
        <div>
          <div className="label-overline mb-2">Describe the complaint (in their words)</div>
          <textarea
            ref={textareaRef}
            value={v.chief_complaint || ""}
            onChange={(e) => onChange({ ...v, chief_complaint: e.target.value })}
            onBlur={() => onSaveDraft?.()}
            rows={3}
            placeholder={'"Sharp pain across my left trap when turning to check blind spot. Started 5 days ago after a long drive."'}
            data-testid={`${testIdPrefix}-text`}
            className="w-full px-4 py-3 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none resize-y text-sm"
          />
        </div>

        {/* Acute vs chronic */}
        <div>
          <div className="label-overline mb-3">Acute or chronic</div>
          <div className="flex gap-2">
            {[
              { id: "acute", label: "Acute", hint: "< 6 weeks" },
              { id: "chronic", label: "Chronic", hint: "> 6 weeks / recurring" },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => onChange({ ...v, issue_type: t.id })}
                data-testid={`${testIdPrefix}-issue-${t.id}`}
                className={`min-h-[48px] flex-1 px-4 rounded-2xl border text-sm transition-all ${
                  v.issue_type === t.id
                    ? "bg-ink-900 text-white border-ink-900"
                    : "bg-white border-paper-200 text-ink-700 hover:border-ink-900/30"
                }`}
              >
                <div className="font-medium">{t.label}</div>
                <div className={`text-xs ${v.issue_type === t.id ? "text-white/70" : "text-ink-500"}`}>{t.hint}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Secondary concerns */}
        <div>
          <div className="label-overline mb-3 text-ink-500">Secondary concerns (optional)</div>
          <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-secondary-chips`}>
            {CHIEF_COMPLAINT_AREAS.filter((a) => a.key !== v.chief_complaint_area).map((a) => {
              const active = (v.secondary_concerns || []).includes(a.key);
              return (
                <button
                  key={a.key}
                  type="button"
                  onClick={() => toggleSecondary(a.key)}
                  data-testid={`${testIdPrefix}-secondary-${a.key}`}
                  className={`min-h-[36px] px-3 rounded-full border text-xs transition-all ${
                    active
                      ? "bg-terracotta-500 text-white border-terracotta-500"
                      : "bg-white border-paper-200 text-ink-700 hover:border-terracotta-500/30"
                  }`}
                >
                  {a.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
